/** Matches a calendar date at the start of an ISO string (YYYY-MM-DD). */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Parse a date string as a local calendar date.
 * Date-only values (e.g. 2026-06-15 from Postgres) are interpreted in the
 * local timezone so they do not shift to the previous day in US timezones.
 */
export function parseLocalDate(dateStr: string): Date {
  const match = DATE_ONLY_RE.exec(dateStr);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
}

export function toDateOnlyString(dateStr: string): string {
  return dateStr.slice(0, 10);
}

export function isSameLocalDate(start: string, end: string): boolean {
  return toDateOnlyString(start) === toDateOnlyString(end);
}

export function formatLocalDate(
  dateStr: string,
  options: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  },
  locale = 'en-US'
): string {
  return parseLocalDate(dateStr).toLocaleDateString(locale, options);
}

/**
 * Format a start/end pair for display. Single-day ranges show one date only.
 */
export function formatLocalDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string | null {
  if (!start || !end) return null;
  if (isSameLocalDate(start, end)) {
    return formatLocalDate(start, options);
  }
  return `${formatLocalDate(start, options)} - ${formatLocalDate(end, options)}`;
}

export function localDateToMs(dateStr: string): number {
  return parseLocalDate(dateStr).getTime();
}

/** True when a signup option covers a single calendar day (same start/end or start only). */
export function isOneDaySignupOption(
  start: string | null | undefined,
  end: string | null | undefined
): boolean {
  if (!start) return false;
  if (!end) return true;
  return isSameLocalDate(start, end);
}

/** Format HH:MM or HH:MM:SS for display (e.g. 7:00 PM). */
export function formatLocalTime(
  timeStr: string | null | undefined,
  locale = 'en-US'
): string | null {
  if (!timeStr?.trim()) return null;
  const [hourPart, minutePart] = timeStr.split(':');
  const hours = Number(hourPart);
  const minutes = Number(minutePart);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  const date = new Date(2000, 0, 1, hours, minutes, 0);
  return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}

export function toTimeInputValue(timeStr: string | null | undefined): string {
  if (!timeStr?.trim()) return '';
  return timeStr.slice(0, 5);
}

export function normalizeTimeForDb(timeStr: string | null | undefined): string | null {
  const trimmed = timeStr?.trim();
  if (!trimmed) return null;
  return trimmed.length === 5 ? `${trimmed}:00` : trimmed;
}

type SignupOptionSchedule = {
  start_date: string | null;
  end_date: string | null;
  start_time?: string | null;
  end_time?: string | null;
};

/** Date (and optional time) label for a registration option. */
export function formatSignupOptionSchedule(option: SignupOptionSchedule): string | null {
  const datePart =
    option.start_date && option.end_date
      ? formatLocalDateRange(option.start_date, option.end_date)
      : option.start_date
        ? formatLocalDate(option.start_date)
        : null;

  if (!datePart) return null;

  if (!isOneDaySignupOption(option.start_date, option.end_date)) {
    return datePart;
  }

  const startTime = formatLocalTime(option.start_time);
  const endTime = formatLocalTime(option.end_time);

  if (startTime && endTime) return `${datePart}, ${startTime} - ${endTime}`;
  if (startTime) return `${datePart}, ${startTime}`;
  return datePart;
}
